import { useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/15";

const Row = ({ label, hint, children }) => (
  <label className="block">
    <span className="mb-1 block text-[11px] font-medium uppercase tracking-widest text-slate-500">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
  </label>
);

/** Platform-owner edit dialog for a client: account name, admin sign-in email and location contact info. */
export function EditClientDialog({ open, onClose, company, building, onSaved }) {
  const [f, setF] = useState({
    name: company.name || "", owner_email: company.owner_email || "",
    location_name: building?.name || "", address: building?.address || "",
    phone: building?.phone || "", reply_email: building?.email || "",
  });
  const [busy, setBusy] = useState(false);
  const emailChanged = f.owner_email.trim().toLowerCase() !== (company.owner_email || "").toLowerCase();

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/platform/company/${company.id}/profile`, {
        name: f.name.trim(), owner_email: f.owner_email.trim(), location_name: f.location_name.trim(),
        address: f.address.trim(), phone: f.phone.trim(), reply_email: f.reply_email.trim(),
      });
      toast.success(emailChanged ? "Client updated — change notice emailed to both addresses" : "Client updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="edit-client-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit client</DialogTitle>
          <DialogDescription>Update the account name, the admin's sign-in email and this location's contact details.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Row label="Account name">
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required maxLength={160}
                   data-testid="edit-client-name" className={inputCls} />
          </Row>
          <Row label="Admin sign-in email"
               hint={emailChanged ? "Both the old and the new address will get a change notice. The password stays the same." : "The email this client's administrator signs in with."}>
            <input type="email" value={f.owner_email} onChange={(e) => setF({ ...f, owner_email: e.target.value })} required maxLength={160}
                   data-testid="edit-client-owner-email" className={inputCls} />
          </Row>
          <Row label="Location name" hint="Shown on the kiosk, reports and invoices. Leave blank to reuse the account name.">
            <input value={f.location_name} onChange={(e) => setF({ ...f, location_name: e.target.value })} maxLength={160}
                   data-testid="edit-client-location-name" className={inputCls} />
          </Row>
          <Row label="Address" hint="Changing the address re-verifies it with Google the next time the kiosk is opened.">
            <textarea value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} maxLength={400} rows={2}
                      data-testid="edit-client-address" className={`${inputCls} h-auto py-2`} />
          </Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Phone">
              <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} maxLength={40}
                     data-testid="edit-client-phone" className={inputCls} />
            </Row>
            <Row label="Reply-to email">
              <input type="email" value={f.reply_email} onChange={(e) => setF({ ...f, reply_email: e.target.value })} maxLength={160}
                     data-testid="edit-client-reply-email" className={inputCls} />
            </Row>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} data-testid="edit-client-cancel"
                    className="h-10 rounded-lg border border-slate-200 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={busy} data-testid="edit-client-save"
                    className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-white disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Same, for a corporate (multi-location) account. */
export function EditCorporateDialog({ open, onClose, corporate, onSaved }) {
  const [f, setF] = useState({ name: corporate.name || "", owner_email: corporate.billing_email || "" });
  const [busy, setBusy] = useState(false);
  const emailChanged = f.owner_email.trim().toLowerCase() !== (corporate.billing_email || "").toLowerCase();

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/platform/corporate/${corporate.id}/profile`, { name: f.name.trim(), owner_email: f.owner_email.trim() });
      toast.success(emailChanged ? "Corporate account updated — change notice emailed to both addresses" : "Corporate account updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="edit-corporate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit corporate account</DialogTitle>
          <DialogDescription>Update the corporate name and the owner's sign-in / billing email.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Row label="Corporate name">
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required maxLength={160}
                   data-testid="edit-corporate-name" className={inputCls} />
          </Row>
          <Row label="Owner sign-in / billing email"
               hint={emailChanged ? "Both the old and the new address will get a change notice." : "Used for sign-in and all billing receipts."}>
            <input type="email" value={f.owner_email} onChange={(e) => setF({ ...f, owner_email: e.target.value })} required maxLength={160}
                   data-testid="edit-corporate-owner-email" className={inputCls} />
          </Row>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} data-testid="edit-corporate-cancel"
                    className="h-10 rounded-lg border border-slate-200 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={busy} data-testid="edit-corporate-save"
                    className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-white disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
